// Keyboard shortcuts in one vocabulary for the site and the desktop app: what
// they do, their keys on each platform, reading a key press, showing keys the way the system
// does, and the keys better left to the system, the browser, and every app's editing. Kept free
// of dependencies, like text.ts, so clients can use it cheaply.
//
// Shortcuts are written as Electron accelerators, such as "Control+Shift+W", with modifiers in
// the order Control, Alt, Shift, then Command on a Mac or Super (the Windows key) elsewhere.

export type Platform = 'mac' | 'windows' | 'linux';

/** The platform, from Node's `process.platform` or a browser's user agent. */
export function platformOf(value: string): Platform {
  if (value === 'darwin' || /Mac|iPhone|iPad/.test(value)) return 'mac';
  if (value === 'win32' || value.includes('Windows')) return 'windows';
  return 'linux';
}

/** What the editor's own shortcuts do, wherever the editor is. */
export const SHORTCUT_ACTIONS = [
  'rewrite',
  'useVersion',
  'clearDraft',
  'fullScreen',
  'showShortcuts',
] as const;
export type ShortcutAction = (typeof SHORTCUT_ACTIONS)[number];
export type Shortcuts = Readonly<Record<ShortcutAction, string>>;

/** How many versions a rewrite has, each with its number key. */
const VERSIONS = 4;

/**
 * The same action has the same keys everywhere: ⌘ on a Mac where other systems use Ctrl.
 * `useVersion` names version 1's keys; the others take the same modifiers with their number.
 * ⌥⌘ rather than ⌥ on a Mac, where ⌥1 types "¡"; Ctrl+Alt rather than Alt on Linux, where
 * browsers switch tabs with Alt and a number. Full screen is Rewrite's keys with ⇧: the editor
 * made bigger, in the same place on the keyboard.
 */
export const DEFAULT_SHORTCUTS: Readonly<Record<Platform, Shortcuts>> = {
  mac: {
    rewrite: 'Command+Enter',
    useVersion: 'Alt+Command+1',
    clearDraft: 'Shift+Command+Backspace',
    fullScreen: 'Shift+Command+Enter',
    showShortcuts: 'Command+/',
  },
  windows: {
    rewrite: 'Control+Enter',
    useVersion: 'Alt+1',
    clearDraft: 'Control+Shift+Backspace',
    fullScreen: 'Control+Shift+Enter',
    showShortcuts: 'Control+/',
  },
  linux: {
    rewrite: 'Control+Enter',
    useVersion: 'Control+Alt+1',
    clearDraft: 'Control+Shift+Backspace',
    fullScreen: 'Control+Shift+Enter',
    showShortcuts: 'Control+/',
  },
};

/**
 * The desktop app's shortcuts that work in every other app.
 * ⌃⇧ on a Mac, since VoiceOver and window managers such as Rectangle and Magnet use ⌃⌥, and ⌥
 * types characters; Alt+Shift elsewhere, since Ctrl+Alt is AltGr, which types characters on
 * many keyboards. W for writing, D for dictation.
 */
export const DEFAULT_GLOBAL_SHORTCUTS: Readonly<
  Record<Platform, { readonly rewrite: string; readonly dictate: string }>
> = {
  mac: { rewrite: 'Control+Shift+W', dictate: 'Control+Shift+D' },
  windows: { rewrite: 'Alt+Shift+W', dictate: 'Alt+Shift+D' },
  linux: { rewrite: 'Alt+Shift+W', dictate: 'Alt+Shift+D' },
};

/** Saved shortcuts over the defaults, keeping only those that still make sense. */
export function resolveShortcuts(saved: unknown, platform: Platform): Shortcuts {
  const record: Readonly<Record<string, unknown>> =
    typeof saved === 'object' && saved !== null ? (saved as Record<string, unknown>) : {};
  const defaults = DEFAULT_SHORTCUTS[platform];
  const shortcuts = { ...defaults };
  for (const action of SHORTCUT_ACTIONS) {
    const value = record[action];
    const parsed = typeof value === 'string' ? parse(value, platform) : null;
    if (parsed && (action !== 'useVersion' || parsed.key === '1')) {
      shortcuts[action] = write(parsed, platform);
    }
  }
  return shortcuts;
}

type Modifier = 'Control' | 'Alt' | 'Shift' | 'Meta';
const MODIFIER_ORDER: readonly Modifier[] = ['Control', 'Alt', 'Shift', 'Meta'];

interface Parsed {
  readonly modifiers: ReadonlySet<Modifier>;
  readonly key: string;
}

const MODIFIER_NAMES: Readonly<Record<string, Modifier | 'Mod'>> = {
  Control: 'Control',
  Ctrl: 'Control',
  MacCtrl: 'Control',
  Alt: 'Alt',
  Option: 'Alt',
  Shift: 'Shift',
  Command: 'Meta',
  Cmd: 'Meta',
  Super: 'Meta',
  Meta: 'Meta',
  CommandOrControl: 'Mod',
  CmdOrCtrl: 'Mod',
  Mod: 'Mod',
};

const PUNCTUATION: Readonly<Record<string, string>> = {
  Minus: '-',
  Equal: '=',
  BracketLeft: '[',
  BracketRight: ']',
  Backslash: '\\',
  Semicolon: ';',
  Quote: "'",
  Comma: ',',
  Period: '.',
  Slash: '/',
  Backquote: '`',
};
const NAMED_KEYS: Readonly<Record<string, string>> = {
  Space: 'Space',
  Enter: 'Enter',
  NumpadEnter: 'Enter',
  Backspace: 'Backspace',
  Delete: 'Delete',
};
const KEYS = new Set([...Object.values(PUNCTUATION), ...Object.values(NAMED_KEYS)]);

/** A shortcut's key for a physical key, whatever the layout, or null for one it can't end with. */
function keyOf(code: string): string | null {
  const match = /^(?:Key([A-Z])|Digit(\d)|Numpad(\d))$/.exec(code);
  if (match) return match[1] ?? match[2] ?? match[3] ?? null;
  if (/^F(?:[1-9]|1\d|2[0-4])$/.test(code)) return code;
  return NAMED_KEYS[code] ?? PUNCTUATION[code] ?? null;
}

function parse(shortcut: string, platform: Platform): Parsed | null {
  // "Control++" would be Plus, which keyOf never makes; split on the separators alone.
  const parts = shortcut.split('+');
  const key = parts.pop();
  if (!key) return null;
  const modifiers = new Set<Modifier>();
  for (const part of parts) {
    const name = MODIFIER_NAMES[part];
    if (!name) return null;
    modifiers.add(name === 'Mod' ? (platform === 'mac' ? 'Meta' : 'Control') : name);
  }
  const normalized = key.length === 1 ? key.toUpperCase() : key === 'Return' ? 'Enter' : key;
  if (!KEYS.has(normalized) && !/^(?:[A-Z0-9]|F(?:[1-9]|1\d|2[0-4]))$/.test(normalized))
    return null;
  return { modifiers, key: normalized };
}

function write({ modifiers, key }: Parsed, platform: Platform): string {
  return [
    ...MODIFIER_ORDER.filter((modifier) => modifiers.has(modifier)).map((modifier) =>
      modifier === 'Meta' ? (platform === 'mac' ? 'Command' : 'Super') : modifier,
    ),
    key,
  ].join('+');
}

/** The parts of a keyboard event a shortcut is made of. */
export interface KeyPress {
  readonly code: string;
  readonly ctrlKey: boolean;
  readonly altKey: boolean;
  readonly shiftKey: boolean;
  readonly metaKey: boolean;
}

function pressed(press: KeyPress): Parsed | null {
  const key = keyOf(press.code);
  if (!key) return null;
  const modifiers = new Set<Modifier>();
  if (press.ctrlKey) modifiers.add('Control');
  if (press.altKey) modifiers.add('Alt');
  if (press.shiftKey) modifiers.add('Shift');
  if (press.metaKey) modifiers.add('Meta');
  return { modifiers, key };
}

const sameModifiers = (a: ReadonlySet<Modifier>, b: ReadonlySet<Modifier>) =>
  a.size === b.size && [...a].every((modifier) => b.has(modifier));

/** The shortcut a key press makes, or null while only modifiers are down. */
export function shortcutFromKeyPress(press: KeyPress, platform: Platform): string | null {
  const parsed = pressed(press);
  return parsed && write(parsed, platform);
}

/** Whether a key press is the shortcut, by physical key, so it works on any keyboard layout. */
export function matchesShortcut(press: KeyPress, shortcut: string, platform: Platform): boolean {
  const parsed = parse(shortcut, platform);
  const got = pressed(press);
  return (
    parsed !== null && got?.key === parsed.key && sameModifiers(got.modifiers, parsed.modifiers)
  );
}

/**
 * Which version a key press picks with the `useVersion` shortcut, as an index from 0, or null.
 * The number row and the keypad both count.
 */
export function versionFromKeyPress(
  press: KeyPress,
  shortcut: string,
  platform: Platform,
  count = VERSIONS,
): number | null {
  const parsed = parse(shortcut, platform);
  const got = pressed(press);
  if (!parsed || !got || !sameModifiers(got.modifiers, parsed.modifiers)) return null;
  const number = Number(got.key);
  return Number.isInteger(number) && number >= 1 && number <= count ? number - 1 : null;
}

/** The shortcut with another key, as `useVersion` makes one for each version. */
export function withKey(shortcut: string, key: string, platform: Platform): string {
  const parsed = parse(shortcut, platform);
  return parsed ? write({ ...parsed, key }, platform) : shortcut;
}

const MAC_SYMBOLS: Readonly<Record<Modifier, string>> = {
  Control: '⌃',
  Alt: '⌥',
  Shift: '⇧',
  Meta: '⌘',
};
const MAC_KEYS: Readonly<Record<string, string>> = {
  Enter: '↵',
  Backspace: '⌫',
  Delete: '⌦',
  Space: 'Space',
};
const PC_NAMES: Readonly<Record<Modifier, string>> = {
  Control: 'Ctrl',
  Alt: 'Alt',
  Shift: 'Shift',
  Meta: 'Win',
};

/** A shortcut as the system writes it: ⇧⌘⌫ on a Mac, Ctrl+Shift+Backspace elsewhere. */
export function formatShortcut(shortcut: string, platform: Platform, key?: string): string {
  const parsed = parse(shortcut, platform);
  if (!parsed) return shortcut;
  const shown = key ?? parsed.key;
  const modifiers = MODIFIER_ORDER.filter((modifier) => parsed.modifiers.has(modifier));
  if (platform === 'mac') {
    return modifiers.map((modifier) => MAC_SYMBOLS[modifier]).join('') + (MAC_KEYS[shown] ?? shown);
  }
  return [
    ...modifiers.map((modifier) =>
      modifier === 'Meta' && platform === 'linux' ? 'Super' : PC_NAMES[modifier],
    ),
    shown,
  ].join('+');
}

/** The `useVersion` keys for every version at once, such as ⌥⌘1–4 or Alt+1–4. */
export const formatVersionShortcut = (shortcut: string, platform: Platform, count = VERSIONS) =>
  formatShortcut(shortcut, platform, `1–${String(count)}`);

/** The shortcut for `aria-keyshortcuts`, such as "Alt+Meta+1". */
export function ariaShortcut(shortcut: string, platform: Platform): string {
  const parsed = parse(shortcut, platform);
  if (!parsed) return '';
  return [
    ...MODIFIER_ORDER.filter((modifier) => parsed.modifiers.has(modifier)),
    parsed.key === 'Space' ? 'Space' : parsed.key,
  ].join('+');
}

/**
 * Where a shortcut works: in a web page, in Lyrmo's own window, or system-wide in every app.
 * Each reaches further, and so has more to stay clear of.
 */
export type ShortcutScope = 'page' | 'app' | 'global';

/**
 * Why a shortcut is better left alone:
 * - modifier: it needs Ctrl, Alt, or ⌘ (system-wide, two modifiers), or typing would set it off
 * - character: on this system those keys type a character, such as ⌥1 for "¡" or AltGr+W for "|"
 * - standard: every app uses it, such as for copy, paste, undo, save, or quit
 * - browser: the browser uses it, such as to switch tabs
 * - system: the system uses it, such as for Spotlight, VoiceOver, or the Windows key
 * - apps: system-wide, it would take keys that apps use for their own commands
 */
export type ShortcutConflict =
  'modifier' | 'character' | 'standard' | 'browser' | 'system' | 'apps';

// Lists of what's taken, written with Mod for ⌘ on a Mac and Ctrl elsewhere. They name the
// shortcuts most people rely on; they can't name every app's.
const STANDARD = [
  'Mod+A',
  'Mod+C',
  'Mod+V',
  'Mod+X',
  'Mod+Z',
  'Mod+Shift+Z',
  'Mod+Shift+V',
  'Mod+B',
  'Mod+I',
  'Mod+U',
  'Mod+F',
  'Mod+S',
  'Mod+P',
  'Mod+O',
  'Mod+N',
  'Mod+W',
  'Mod+Q',
  'Mod+Backspace',
  'Mod+Delete',
  // The editor's formatting, as in Docs and Slack: strikethrough, clearing it, and numbers,
  // bullets, and boxes.
  'Mod+Shift+X',
  'Mod+\\',
  'Mod+Shift+7',
  'Mod+Shift+8',
  'Mod+Shift+9',
];
const STANDARD_MAC = [
  'Command+H',
  'Command+M',
  'Command+,',
  'Alt+Command+H',
  'Alt+Command+W',
  'Alt+Backspace',
  'Alt+Delete',
];
const STANDARD_PC = ['Control+Y', 'Alt+F4'];
// ⌃ with these letters edits text in every Mac text field: ⌃A start of line, ⌃K delete to end…
const MAC_TEXT_KEYS = new Set('ABDEFHKNOPTVY');

const BROWSER = [
  'Mod+T',
  'Mod+Shift+T',
  'Mod+Shift+N',
  'Mod+Shift+W',
  'Mod+L',
  'Mod+R',
  'Mod+Shift+R',
  'Mod+D',
  'Mod+Shift+D',
  'Mod+J',
  'Mod+K',
  'Mod+E',
  'Mod+G',
  'Mod+Shift+G',
  'Mod+H',
  'Mod+Y',
  'Mod+Shift+A',
  'Mod+Shift+B',
  'Mod+Shift+O',
  'Mod+Shift+M',
  'Mod+Shift+I',
  'Mod+Shift+J',
  'Mod+Shift+C',
  'Mod+Shift+Delete',
  'Mod+=',
  'Mod+-',
  'Mod+0',
  'Mod+[',
  'Mod+]',
  'F5',
  'F6',
  'F11',
  'F12',
];
const BROWSER_MAC = ['Alt+Command+I', 'Alt+Command+J', 'Alt+Command+C', 'Alt+Command+U'];
const BROWSER_PC = ['Alt+D', 'Alt+E', 'Alt+F', 'Alt+Home', 'Control+F5'];

const SYSTEM_MAC = [
  'Command+Space',
  'Control+Space',
  'Alt+Command+Space',
  'Command+`',
  'Shift+Command+Q',
  'Shift+Command+3',
  'Shift+Command+4',
  'Shift+Command+5',
  'Shift+Command+6',
  'Control+Shift+Command+3',
  'Control+Shift+Command+4',
  'Control+Command+Q',
  'Control+Command+Space',
  'Control+Command+F',
  'Control+Command+D',
  'Alt+Command+D',
  'Alt+Command+8',
  'Alt+Command+=',
  'Alt+Command+-',
];
const SYSTEM_WINDOWS = ['Alt+Space', 'Control+Alt+Delete'];
const SYSTEM_LINUX = [
  'Alt+Space',
  'Alt+F1',
  'Alt+F2',
  'Alt+F7',
  'Alt+F8',
  'Alt+F10',
  'Control+Alt+T',
  'Control+Alt+D',
  'Control+Alt+L',
  'Control+Alt+Delete',
  ...Array.from({ length: 12 }, (_, index) => `Control+Alt+F${String(index + 1)}`),
];

const cache = new Map<string, ReadonlySet<string>>();
/** A list as written shortcuts for the platform, so they compare with `write`'s. */
function taken(name: string, platform: Platform, list: readonly string[]): ReadonlySet<string> {
  const id = `${name}:${platform}`;
  let set = cache.get(id);
  if (!set) {
    set = new Set(
      list.flatMap((shortcut) => {
        const parsed = parse(shortcut, platform);
        return parsed ? [write(parsed, platform)] : [];
      }),
    );
    cache.set(id, set);
  }
  return set;
}

const TYPED = /^(?:[A-Z0-9]|Space|[-=[\]\\;',./`])$/;

/** Why a shortcut is better left alone where it would work, or null if it's clear. */
export function shortcutConflict(
  shortcut: string,
  platform: Platform,
  scope: ShortcutScope,
): ShortcutConflict | null {
  const parsed = parse(shortcut, platform);
  if (!parsed) return 'modifier';
  const { modifiers, key } = parsed;
  const has = (modifier: Modifier) => modifiers.has(modifier);
  const only = (...expected: Modifier[]) => sameModifiers(modifiers, new Set(expected));
  const mac = platform === 'mac';
  const mod: Modifier = mac ? 'Meta' : 'Control';
  const functionKey = /^F\d+$/.test(key);
  const written = write(parsed, platform);

  // Typing never sets one off.
  if (!functionKey && !(has('Control') || has('Alt') || has('Meta'))) return 'modifier';

  // ⌥ types characters on a Mac; Ctrl+Alt is AltGr on Windows, which types them too.
  if (TYPED.test(key)) {
    if (mac && has('Alt') && !has('Control') && !has('Meta')) return 'character';
    if (platform === 'windows' && has('Control') && has('Alt') && !has('Meta')) return 'character';
  }

  // VoiceOver commands are ⌃⌥ with a key, as are window managers'; the Windows key and Super
  // belong to the system.
  if (mac ? has('Control') && has('Alt') : has('Meta')) return 'system';
  const system = mac ? SYSTEM_MAC : platform === 'windows' ? SYSTEM_WINDOWS : SYSTEM_LINUX;
  if (taken('system', platform, system).has(written)) return 'system';

  if (
    taken('standard', platform, [...STANDARD, ...(mac ? STANDARD_MAC : STANDARD_PC)]).has(written)
  )
    return 'standard';
  if (mac && only('Control') && MAC_TEXT_KEYS.has(key)) return 'standard';

  if (scope === 'page') {
    // Browsers go to a tab with Ctrl or ⌘ and its number, and on Linux with Alt too.
    if (/^[1-9]$/.test(key) && (only(mod) || (platform === 'linux' && only('Alt'))))
      return 'browser';
    if (taken('browser', platform, [...BROWSER, ...(mac ? BROWSER_MAC : BROWSER_PC)]).has(written))
      return 'browser';
  }

  // System-wide, one modifier is still too easy to press by chance.
  if (scope === 'global' && modifiers.size < (functionKey ? 1 : 2)) return 'modifier';

  // System-wide, these modifiers would take keys from apps' own commands: ⌘ and ⇧⌘ or ⌥⌘ on a
  // Mac, Ctrl and Ctrl+Shift, or Alt for menus, elsewhere.
  if (scope === 'global') {
    const appModifiers = mac
      ? only('Meta') || only('Shift', 'Meta') || only('Alt', 'Meta') || only('Alt', 'Shift', 'Meta')
      : only('Control') || only('Control', 'Shift') || only('Alt');
    if (appModifiers) return 'apps';
  }
  return null;
}

/** The first conflict among the `useVersion` keys for every version. */
export function versionShortcutConflict(
  shortcut: string,
  platform: Platform,
  scope: ShortcutScope,
  count = VERSIONS,
): ShortcutConflict | null {
  for (let number = 1; number <= count; number += 1) {
    const conflict = shortcutConflict(withKey(shortcut, String(number), platform), platform, scope);
    if (conflict) return conflict;
  }
  return null;
}

/** Whether two shortcuts are the same keys, however they're written. */
export function sameShortcut(a: string, b: string, platform: Platform): boolean {
  const first = parse(a, platform);
  const second = parse(b, platform);
  return Boolean(first && second && write(first, platform) === write(second, platform));
}

interface UsedKeys {
  readonly action: ShortcutAction;
  readonly keys: string;
}

/** Every key combination a set of shortcuts uses, with `useVersion` once for each version. */
export function shortcutKeys(
  shortcuts: Shortcuts,
  platform: Platform,
  count = VERSIONS,
): readonly UsedKeys[] {
  return SHORTCUT_ACTIONS.flatMap((action): UsedKeys[] =>
    action === 'useVersion'
      ? Array.from({ length: count }, (_, index) => ({
          action,
          keys: withKey(shortcuts[action], String(index + 1), platform),
        }))
      : [{ action, keys: shortcuts[action] }],
  );
}

/** Explains a conflict in English for the desktop app. */
export function describeShortcutConflict(
  conflict: ShortcutConflict,
  keys: string,
  platform: Platform,
  scope: ShortcutScope,
): string {
  const modifiers = platform === 'mac' ? '⌃, ⌥, or ⌘' : 'Ctrl or Alt';
  switch (conflict) {
    case 'modifier':
      return scope === 'global'
        ? `Use two modifier keys, including ${modifiers}, so ${keys} isn’t pressed by chance.`
        : `Hold ${modifiers} with the key, so typing never sets ${keys} off.`;
    case 'character':
      return `${keys} types a character on this keyboard.`;
    case 'standard':
      return `${keys} is a standard shortcut in every app, such as copy, paste, or undo.`;
    case 'browser':
      return `${keys} is a browser shortcut.`;
    case 'system':
      return platform === 'mac'
        ? `${keys} is used by macOS, VoiceOver, or window managers.`
        : `${keys} is used by the system.`;
    case 'apps':
      return `${keys} would take a shortcut from other apps. Add another modifier key.`;
  }
}
